import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Upload,
  LayoutGrid,
  CreditCard,
  Wallet,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from "lucide-react";
import { http } from "@/app/services/api";
import { NAV_ITEMS } from "@/app/config/routes";
import { cn } from "@/app/utils/tw-merge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/ui/tooltip";
import type { HealthStatus } from "@/app/types/api";

const ICONS: Record<string, LucideIcon> = {
  upload: Upload,
  apps: LayoutGrid,
  confirm: CreditCard,
  plans: Wallet,
};

const COLLAPSE_KEY = "lumina_mkt_sidebar_collapsed";

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();

  const [collapsed, setCollapsed] = React.useState<boolean>(loadCollapsed);
  const toggle = React.useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Health query vẫn ở đây cho footer collapsed (khi key pill + status
  // dot nằm ở header thì footer collapsed rỗng — giữ query để future
  // use, hoặc có thể xoá nếu muốn).
  useQuery({
    queryKey: ["health"],
    queryFn: () => http.get<HealthStatus>("/health"),
    refetchInterval: 30_000,
    retry: false,
  });

  return (
    <aside
      className={cn(
        "group relative hidden shrink-0 flex-col bg-transparent text-sidebar-foreground transition-[width] duration-200 ease-out md:flex",
        collapsed ? "w-[60px]" : "w-60"
      )}
    >
      {/* Floating collapse pill — Core idiom: ẩn cho tới khi hover sidebar,
          focus-visible luôn hiện để user bàn phím truy cập được. */}
      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
        className="absolute right-[-14px] top-4 z-30 grid h-7 w-7 place-items-center rounded-full border border-border bg-card text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 group-hover:opacity-100"
      >
        {collapsed ? (
          <PanelLeftOpen className="h-3.5 w-3.5" />
        ) : (
          <PanelLeftClose className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Nav — không có logo/brand/key-pill nữa, chúng đã lên AppHeader. */}
      <nav className="mt-2 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2">
        <ul className="space-y-0.5">
          {NAV_ITEMS.map(({ to, i18nKey, match }) => {
            const Icon = ICONS[i18nKey.replace("nav.", "")] ?? Upload;
            const active = match(location.pathname);
            const label = t(i18nKey);
            const link = (
              <NavLink
                to={to}
                aria-label={collapsed ? label : undefined}
                className={cn(
                  "flex w-full items-center rounded-lg text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
                  collapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2.5",
                  active
                    ? "bg-gradient-to-r from-primary to-primary/55 text-white shadow-md"
                    : "text-muted-foreground hover:bg-primary/10 hover:text-primary"
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span className="truncate">{label}</span>}
              </NavLink>
            );
            return (
              <li key={to}>
                {collapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                ) : (
                  link
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
